<template>
  <div>
    <h3 class="mt-4 page-header-2">Actual Times</h3>
    <div class="overflow-auto">
      <AppTable class="mt-2 job-times-table">
        <thead>
          <tr>
            <th>Scheduled</th>
            <th>Appointment #</th>
            <th>Status</th>
            <th>Service Category</th>
            <th>Tech and Crew Members</th>
            <th v-if="preWorkEnabled" class="duration-time-header">Pre-Work</th>
            <th class="duration-time-header">Travel</th>
            <th class="duration-time-header">Work</th>
            <th class="duration-time-header">Duration</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="appointment in job.appointments"
            :key="appointment.id"
            :class="{ 'table-hint-cell': !isAppointmentFinished(appointment) }"
          >
            <td>{{ formatTableDate(appointment.scheduled_time) }}</td>
            <td>
              <span class="link" @click="openAppt(appointment.id)">{{
                appointment.id
              }}</span>
            </td>
            <td>{{ statusTag(appointment.status) }}</td>
            <td class="service-cell">
              {{ appointment.service_category.name }}
            </td>
            <td style="max-width: 250px">
              <div>
                <div
                  v-for="row in appointmentRowsById[appointment.id] || []"
                  :key="`${appointment.id}-${row.key}-name`"
                  :class="{
                    'mt-2': row.groupStart,
                    'mt-1': row.kind === 'crew'
                  }"
                >
                  <template v-if="row.kind === 'tech'">
                    <truncate-text v-if="getTechLinkURL(row.technician)">
                      <AppLink
                        :to="getTechLinkURL(row.technician)"
                        :text="fullName(row.technician)"
                      />
                    </truncate-text>
                    <span v-else>--</span>
                  </template>
                  <span v-else class="crew-name-row">{{ row.full_name }}</span>
                </div>
                <template
                  v-if="
                    isMultiTechEnabled &&
                    (appointmentOtherCrewById[appointment.id] || []).length
                  "
                >
                  <div class="mt-2 other-crew-label">Other crew</div>
                  <div
                    v-for="crew in appointmentOtherCrewById[appointment.id]"
                    :key="`${appointment.id}-${crew.id}-other-name`"
                    class="mt-1"
                  >
                    <span class="crew-name-row">{{ crew.full_name }}</span>
                  </div>
                </template>
              </div>
            </td>
            <td v-if="preWorkEnabled" class="text-right">
              <div>
                <div
                  v-for="row in appointmentRowsById[appointment.id] || []"
                  :key="`${appointment.id}-${row.key}-pre_work`"
                  :class="{
                    'mt-2': row.groupStart,
                    'mt-1': row.kind === 'crew'
                  }"
                >
                  <minutes-label
                    :minutes="row.pre_work_minutes"
                    :not-available="!job.job_times_valid"
                    :default-text="missingDataTooltip"
                    :not-available-text="notAvailableTooltip"
                  />
                </div>
                <template
                  v-if="
                    isMultiTechEnabled &&
                    (appointmentOtherCrewById[appointment.id] || []).length
                  "
                >
                  <div class="mt-2 other-crew-label-time"></div>
                  <div
                    v-for="crew in appointmentOtherCrewById[appointment.id]"
                    :key="`${appointment.id}-${crew.id}-other-pre`"
                    class="mt-1"
                  >
                    <minutes-label
                      :minutes="crew.pre_work_minutes"
                      :not-available="!job.job_times_valid"
                      :default-text="missingDataTooltip"
                      :not-available-text="notAvailableTooltip"
                    />
                  </div>
                </template>
              </div>
            </td>
            <td class="text-right">
              <div>
                <div
                  v-for="row in appointmentRowsById[appointment.id] || []"
                  :key="`${appointment.id}-${row.key}-travel`"
                  :class="{
                    'mt-2': row.groupStart,
                    'mt-1': row.kind === 'crew'
                  }"
                >
                  <minutes-label
                    :minutes="row.travel_minutes"
                    :not-available="!job.job_times_valid"
                    :default-text="missingDataTooltip"
                    :not-available-text="notAvailableTooltip"
                  />
                </div>
                <template
                  v-if="
                    isMultiTechEnabled &&
                    (appointmentOtherCrewById[appointment.id] || []).length
                  "
                >
                  <div class="mt-2 other-crew-label-time"></div>
                  <div
                    v-for="crew in appointmentOtherCrewById[appointment.id]"
                    :key="`${appointment.id}-${crew.id}-other-travel`"
                    class="mt-1"
                  >
                    <minutes-label
                      :minutes="crew.travel_minutes"
                      :not-available="!job.job_times_valid"
                      :default-text="missingDataTooltip"
                      :not-available-text="notAvailableTooltip"
                    />
                  </div>
                </template>
              </div>
            </td>
            <td class="text-right">
              <div>
                <div
                  v-for="row in appointmentRowsById[appointment.id] || []"
                  :key="`${appointment.id}-${row.key}-work`"
                  :class="{
                    'mt-2': row.groupStart,
                    'mt-1': row.kind === 'crew'
                  }"
                >
                  <minutes-label
                    :minutes="row.work_minutes"
                    :not-available="!job.job_times_valid"
                    :default-text="missingDataTooltip"
                    :not-available-text="notAvailableTooltip"
                  />
                </div>
                <template
                  v-if="
                    isMultiTechEnabled &&
                    (appointmentOtherCrewById[appointment.id] || []).length
                  "
                >
                  <div class="mt-2 other-crew-label-time"></div>
                  <div
                    v-for="crew in appointmentOtherCrewById[appointment.id]"
                    :key="`${appointment.id}-${crew.id}-other-work`"
                    class="mt-1"
                  >
                    <minutes-label
                      :minutes="crew.work_minutes"
                      :not-available="!job.job_times_valid"
                      :default-text="missingDataTooltip"
                      :not-available-text="notAvailableTooltip"
                    />
                  </div>
                </template>
              </div>
            </td>
            <td class="text-right">
              <div>
                <div
                  v-for="row in appointmentRowsById[appointment.id] || []"
                  :key="`${appointment.id}-${row.key}-duration`"
                  :class="{
                    'mt-2': row.groupStart,
                    'mt-1': row.kind === 'crew'
                  }"
                >
                  <minutes-label
                    :minutes="row.duration_minutes"
                    :not-available="!job.job_times_valid"
                    :default-text="missingDataTooltip"
                    :not-available-text="notAvailableTooltip"
                  />
                </div>
                <template
                  v-if="
                    isMultiTechEnabled &&
                    (appointmentOtherCrewById[appointment.id] || []).length
                  "
                >
                  <div class="mt-2 other-crew-label-time"></div>
                  <div
                    v-for="crew in appointmentOtherCrewById[appointment.id]"
                    :key="`${appointment.id}-${crew.id}-other-duration`"
                    class="mt-1"
                  >
                    <minutes-label
                      :minutes="crew.duration_minutes"
                      :not-available="!job.job_times_valid"
                      :default-text="missingDataTooltip"
                      :not-available-text="notAvailableTooltip"
                    />
                  </div>
                </template>
              </div>
            </td>
          </tr>
        </tbody>
        <tfoot>
          <tr>
            <td>Completed Totals</td>
            <td></td>
            <td></td>
            <td></td>
            <td></td>
            <td v-if="preWorkEnabled" class="text-right">
              <minutes-label
                :minutes="completedTotalPreWorkMinutes"
                :not-available="!job.job_times_valid"
                :default-text="missingDataTooltip"
                :not-available-text="notAvailableTooltip"
              />
            </td>
            <td class="text-right">
              <minutes-label
                :minutes="completedTotalTravelMinutes"
                :not-available="!job.job_times_valid"
                :default-text="missingDataTooltip"
                :not-available-text="notAvailableTooltip"
              />
            </td>
            <td class="text-right">
              <minutes-label
                :minutes="completedTotalWorkMinutes"
                :not-available="!job.job_times_valid"
                :default-text="missingDataTooltip"
                :not-available-text="notAvailableTooltip"
              />
            </td>
            <td class="text-right">
              <minutes-label
                :minutes="completedTotalDurationMinutes"
                :not-available="!job.job_times_valid"
                :default-text="missingDataTooltip"
                :not-available-text="notAvailableTooltip"
              />
            </td>
          </tr>
        </tfoot>
      </AppTable>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapState } from "vuex";
import { EventBus } from "@/lib/event_bus.js";
import { buildTechniciansById, normalizeId } from "@/lib/helpers.js";
import { updateQueryParam } from "@/lib/url.js";

import AppLink from "@/components/Link.vue";
import MinutesLabel from "@/components/MinutesLabel.vue";
import AppTable from "@/components/Table.vue";
import TruncateText from "@/components/TruncateText.vue";
import CONSTANTS from "@/Constants.js";
const { NO_JOB_TIME_TOOLTIP_TEXT, NOT_AVAILABLE_JOB_TIME_TOOLTIP_TEXT } =
  CONSTANTS;

export default {
  components: {
    AppTable,
    AppLink,
    MinutesLabel,
    TruncateText
  },
  computed: {
    ...mapGetters(["featureEnabledForTenant", "settingEnabledForTenant"]),
    ...mapState(["job"]),
    isMultiTechEnabled() {
      return this.featureEnabledForTenant("multiple_technicians_ff_enabled");
    },
    preWorkEnabled() {
      return this.settingEnabledForTenant("pre_work_enabled");
    },
    appointmentRowsById() {
      const rowsById = {};
      (this.job.appointments || []).forEach((appointment) => {
        rowsById[appointment.id] = this.buildTechCrewRows(appointment);
      });
      return rowsById;
    },
    appointmentOtherCrewById() {
      const otherCrewById = {};
      (this.job.appointments || []).forEach((appointment) => {
        otherCrewById[appointment.id] =
          this.otherCrewForAppointment(appointment);
      });
      return otherCrewById;
    },
    completedAppointments() {
      return (this.job.appointments || []).filter(
        (appointment) => appointment.status === "completed"
      );
    },
    completedTotalPreWorkMinutes() {
      return this.sumCompletedTotals("total_pre_work_minutes");
    },
    completedTotalTravelMinutes() {
      return this.sumCompletedTotals("total_travel_minutes");
    },
    completedTotalWorkMinutes() {
      return this.sumCompletedTotals("total_work_minutes");
    },
    completedTotalDurationMinutes() {
      return this.sumCompletedTotals("total_duration_minutes");
    },
    missingDataTooltip() {
      return NO_JOB_TIME_TOOLTIP_TEXT;
    },
    notAvailableTooltip() {
      return NOT_AVAILABLE_JOB_TIME_TOOLTIP_TEXT;
    }
  },
  methods: {
    calculateMinutes(startTime, endTime) {
      if (!startTime || !endTime) {
        return null;
      }

      const start = new Date(startTime);
      const end = new Date(endTime);
      if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
        return null;
      }

      return Math.round((end.getTime() - start.getTime()) / 60000);
    },
    preferredMinutes(existingMinutes, startTime, endTime) {
      if (
        existingMinutes !== null &&
        existingMinutes !== undefined &&
        existingMinutes !== ""
      ) {
        return existingMinutes;
      }
      return this.calculateMinutes(startTime, endTime);
    },
    techniciansById(appointment) {
      return buildTechniciansById(appointment);
    },
    appointmentTechniciansForDisplay(appointment) {
      const techniciansById = this.techniciansById(appointment);
      return (
        Array.isArray(appointment.appointment_technicians)
          ? [...appointment.appointment_technicians]
          : []
      )
        .sort((a, b) => (a.position || 0) - (b.position || 0))
        .map((appointmentTechnician) => {
          const technicianProfileId = normalizeId(
            appointmentTechnician.technician_profile_id
          );
          return {
            ...appointmentTechnician,
            technician_profile_id: technicianProfileId,
            technician: techniciansById.get(technicianProfileId) || null
          };
        });
    },
    crewForTechnician(appointment, technicianProfileId) {
      const techId = normalizeId(technicianProfileId);
      return (appointment.crew_members || []).filter((crew) => {
        const apptScopedId = normalizeId(crew?.assigned_technician_profile_id);
        if (apptScopedId != null) {
          return apptScopedId === techId;
        }
        return normalizeId(crew?.assigned_to?.technician_id) === techId;
      });
    },
    otherCrewForAppointment(appointment) {
      if (!this.isMultiTechEnabled) {
        return [];
      }

      const appointmentTechnicians =
        this.appointmentTechniciansForDisplay(appointment);
      if (!appointmentTechnicians.length) {
        return [];
      }

      const assignedTechnicianIds = new Set(
        appointmentTechnicians
          .map((appointmentTechnician) =>
            normalizeId(appointmentTechnician.technician_profile_id)
          )
          .filter((id) => id != null)
      );

      return (appointment.crew_members || []).filter((crew) => {
        const assignedToId = normalizeId(crew?.assigned_to?.technician_id);
        const apptScopedId = normalizeId(crew?.assigned_technician_profile_id);
        if (apptScopedId != null && assignedTechnicianIds.has(apptScopedId)) {
          return false;
        }
        return assignedToId == null || !assignedTechnicianIds.has(assignedToId);
      });
    },
    buildTechRow(tech, source, key, groupStart = false) {
      return {
        key,
        kind: "tech",
        groupStart,
        technician: tech,
        pre_work_minutes: this.preferredMinutes(
          source.pre_work_minutes,
          source.accepted_time,
          source.dispatch_time
        ),
        travel_minutes: this.preferredMinutes(
          source.travel_minutes,
          source.dispatch_time,
          source.arrival_time
        ),
        work_minutes: this.preferredMinutes(
          source.work_minutes,
          source.arrival_time,
          source.departure_time
        ),
        duration_minutes: this.preferredMinutes(
          source.duration_minutes,
          source.dispatch_time,
          source.departure_time
        )
      };
    },
    buildCrewRow(crew, key) {
      return {
        key,
        kind: "crew",
        groupStart: false,
        full_name: crew.full_name,
        pre_work_minutes: crew.pre_work_minutes,
        travel_minutes: crew.travel_minutes,
        work_minutes: crew.work_minutes,
        duration_minutes: crew.duration_minutes
      };
    },
    buildTechCrewRows(appointment) {
      if (this.isMultiTechEnabled) {
        const appointmentTechnicians =
          this.appointmentTechniciansForDisplay(appointment);

        return appointmentTechnicians.flatMap(
          (appointmentTechnician, index) => {
            const rows = [
              this.buildTechRow(
                appointmentTechnician.technician,
                appointmentTechnician,
                `tech-${appointmentTechnician.technician_profile_id || index}`,
                index > 0
              )
            ];
            this.crewForTechnician(
              appointment,
              appointmentTechnician.technician_profile_id
            ).forEach((crew, crewIndex) => {
              rows.push(
                this.buildCrewRow(crew, `crew-${crew.id}-${index}-${crewIndex}`)
              );
            });
            return rows;
          }
        );
      }

      const fallbackTechnician =
        appointment.technician ||
        this.appointmentTechniciansForDisplay(appointment)[0]?.technician ||
        null;

      const rows = [
        this.buildTechRow(fallbackTechnician, appointment, "tech-0")
      ];
      (appointment.crew_members || []).forEach((crew, crewIndex) => {
        rows.push(this.buildCrewRow(crew, `crew-${crew.id}-${crewIndex}`));
      });
      return rows;
    },
    sumCompletedTotals(field) {
      const values = this.completedAppointments
        .map((appointment) => appointment[field])
        .filter(
          (value) => value !== null && value !== undefined && value !== ""
        );

      if (!values.length) {
        return null;
      }

      return values.reduce((sum, value) => sum + (Number(value) || 0), 0);
    },
    async openAppt(apptId) {
      await updateQueryParam("appointment_id", apptId);
      EventBus.$emit("set-selected-tab", "Appointments");
    },
    statusTag(status) {
      return CONSTANTS.APPOINTMENT_STATUS_MAP[status];
    },
    getTechLinkURL(tech) {
      return tech ? this.technicianShowPath(tech.user_id) : "";
    },
    isAppointmentFinished({ status }) {
      return status === "completed" || status === "cancelled";
    }
  }
};
</script>

<style lang="scss" scoped>
table > tbody > tr.table-hint-cell > td {
  font-style: italic;
  color: var(--secondary-color) !important;
}

.service-cell {
  max-width: 350px;
  white-space: initial;
}

.crew-name-row {
  padding-left: 12px;
}

.other-crew-label {
  font-weight: 600;
}

.other-crew-label-time {
  min-height: 24px;
}
</style>
